/**
 * OrgContactAside — the detail page's sticky contact aside (Directory
 * detail rebuild §2). THE card recipe: a small letterspaced "Contact"
 * title, then one row per field — a 32px sage-50 icon tile (sage-600
 * glyph) beside a stacked 10.5px uppercase sage-400 label and its value.
 * Values read ink; phone numbers read forest semibold. Rows divide on
 * hairlines. Empty fields invite with a quiet "Add {field}" rather than a
 * dotted form line.
 *
 * The value slots hold EditableFields, so the same component renders
 * read-only text for CM and hover-to-edit for admins — the useAutoSave
 * wiring is untouched beneath the restyle (per-field editability stays
 * discoverable on hover; chrome-rest).
 *
 * "See something off?" renders only for suggest-capable non-writers.
 * ContactCard + AsideContactRow are exported — IndividualDetail's aside
 * composes the same shell and rows (one card, two composers).
 */

import { useState, type ReactNode } from 'react';
import { Phone, Mail, MapPin, Globe, Printer, Pencil, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { EditableField, type EditableFieldProps } from '@/components/EditableField';
import { emailValidator, phoneValidator, urlValidator } from '@/lib/validators';
import { formatPhoneDisplay } from '@/lib/format';
import type { ProviderDetail } from '@/lib/types';
import {
  PostalAddressDialog,
  type AddressFormParts,
  type AddressSaveHow,
  type PostalSaveOutcome,
} from '@/components/clientRecord/AddressesCard';

type SaveField = EditableFieldProps['onSave'];

export interface OrgContactAsideProps {
  provider: ProviderDetail;
  canEdit: boolean;
  canSuggestChange: boolean;
  onSuggestOpen: () => void;
  onSavePhone: SaveField;
  onSaveEmail: SaveField;
  /** Saves the address as its parts; answers what the save did to the map. */
  onSaveAddress: (parts: AddressFormParts, how: AddressSaveHow) => Promise<PostalSaveOutcome>;
  onSaveWebsite: SaveField;
  onSavePhone2: SaveField;
  onSaveFax: SaveField;
}

/** THE card recipe shell for the contact aside (title + hairline-divided rows). */
export function ContactCard({
  title = 'Contact',
  children,
}: {
  title?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-card border border-sage-800/[8%] bg-paper px-5 py-4 shadow-sh1">
      <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sage-400">
        {title}
      </h2>
      <div className="divide-y divide-sage-800/[8%]">{children}</div>
    </div>
  );
}

/**
 * One labelled row in the contact card: a sage-50 icon tile, a stacked
 * uppercase sage-400 label, and the value slot. `emphasis` renders the
 * value forest-semibold (phone numbers); otherwise ink.
 */
export function AsideContactRow({
  icon: Icon,
  label,
  emphasis = false,
  children,
}: {
  icon: LucideIcon;
  label: string;
  emphasis?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-base bg-sage-50 text-sage-600">
        <Icon size={15} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-sage-400">
          {label}
        </div>
        <div
          className={
            'mt-0.5 break-words text-sm ' +
            (emphasis ? 'font-semibold text-sage-800' : 'font-medium text-ink')
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** A Directory listing's address: its text and its parts. */
export interface ListingAddress {
  address: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
}

/** The parts a listing stores, or null when its address is text only. */
function storedParts(listing: ListingAddress): AddressFormParts | null {
  if ((listing.address_line1 ?? null) === null && (listing.address_city ?? null) === null) return null;
  return {
    line1: listing.address_line1 ?? null,
    line2: listing.address_line2 ?? null,
    city: listing.address_city ?? null,
    state: listing.address_state ?? null,
    zip: listing.address_zip ?? null,
  };
}

/** EXPORTED: a Directory listing's Address row (an organization's or a
 *  person's): the text, and for a seat that writes directly an Edit that
 *  opens the postal address dialog (a Directory address: a town and a
 *  state at least). */
export function DirectoryAddressRow({
  listing,
  canEdit,
  onSave,
  testId = 'org-address-edit',
}: {
  listing: ListingAddress;
  canEdit: boolean;
  onSave: OrgContactAsideProps['onSaveAddress'];
  testId?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const text = listing.address ?? '';
  return (
    <div className="flex items-start justify-between gap-2">
      <span className={text === '' ? 'text-ink-soft' : undefined}>{text === '' ? 'No address' : text}</span>
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          aria-label={text === '' ? 'Add address' : 'Edit address'}
          data-testid={testId}
          onClick={() => setOpen(true)}
        >
          <Pencil size={14} aria-hidden="true" />
        </Button>
      )}
      {open && (
        <PostalAddressDialog
          existing={storedParts(listing)}
          existingText={listing.address}
          rule="directory"
          onSave={onSave}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export function OrgContactAside({
  provider,
  canEdit,
  canSuggestChange,
  onSuggestOpen,
  onSavePhone,
  onSaveEmail,
  onSaveAddress,
  onSaveWebsite,
  onSavePhone2,
  onSaveFax,
}: OrgContactAsideProps): ReactNode {
  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-[84px]">
      <ContactCard>
        <AsideContactRow icon={Phone} label="Phone" emphasis>
          <EditableField value={provider.phone} onSave={onSavePhone} label="Phone" validate={phoneValidator} placeholder="Add phone" format={formatPhoneDisplay} canEdit={canEdit} />
        </AsideContactRow>
        <AsideContactRow icon={Mail} label="Email">
          <EditableField value={provider.email} onSave={onSaveEmail} label="Email" validate={emailValidator} placeholder="Add email" canEdit={canEdit} />
        </AsideContactRow>
        <AsideContactRow icon={MapPin} label="Address">
          <DirectoryAddressRow listing={provider} canEdit={canEdit} onSave={onSaveAddress} />
        </AsideContactRow>
        <AsideContactRow icon={Globe} label="Website">
          <EditableField value={provider.website} onSave={onSaveWebsite} label="Website" validate={urlValidator} placeholder="Add website" canEdit={canEdit} />
        </AsideContactRow>
        {(provider.phone_2 || canEdit) && (
          <AsideContactRow icon={Phone} label="Secondary phone" emphasis>
            <EditableField value={provider.phone_2} onSave={onSavePhone2} label="Secondary phone" validate={phoneValidator} placeholder="Add secondary phone" format={formatPhoneDisplay} canEdit={canEdit} />
          </AsideContactRow>
        )}
        {(provider.fax || canEdit) && (
          <AsideContactRow icon={Printer} label="Fax" emphasis>
            <EditableField value={provider.fax} onSave={onSaveFax} label="Fax" validate={phoneValidator} placeholder="Add fax" format={formatPhoneDisplay} canEdit={canEdit} />
          </AsideContactRow>
        )}
      </ContactCard>

      {!canEdit && canSuggestChange && (
        <div className="rounded-card border border-sage-800/[6%] bg-sand py-[18px] px-[22px]">
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">See something off?</div>
          <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">
            Phone changed, or they stopped taking referrals? Let an admin know.
          </p>
          <Button
            variant="secondary"
            className="w-full"
            icon={<Pencil size={15} aria-hidden="true" />}
            onClick={onSuggestOpen}
            data-testid="aside-suggest-change"
          >
            Suggest a change
          </Button>
        </div>
      )}
    </aside>
  );
}
